import React, { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/** Inline-Editor für Onboarding/Offboarding UE-Ziel + Coach — rein intern zur Umsetzungs-
 * Unterstützung. Schreibt NUR onboarding_ue/onboarding_user_id (bzw. offboarding_*) auf den
 * Kunden, NICHT in die Vertragsdaten (der Vertrag/Preisliste liest nur `sections`, siehe
 * contracts.py `_module_rows` — Onboarding/Offboarding fließen dort nie ein). */
export default function OnboardingAssignEditor({ clientId, kind, ueTarget, coachUserId, coaches, onSaved, readOnly, testId }) {
  const [editing, setEditing] = useState(false);
  const [ue, setUe] = useState(ueTarget || 0);
  const [coach, setCoach] = useState(coachUserId || "");
  const [saving, setSaving] = useState(false);

  const startEdit = () => { setUe(ueTarget || 0); setCoach(coachUserId || ""); setEditing(true); };

  const save = async () => {
    const v = parseFloat(ue);
    if (Number.isNaN(v) || v < 0) { toast.error("Ungültiger UE-Wert"); return; }
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}`, {
        [`${kind}_ue`]: v,
        [`${kind}_user_id`]: coach || null,
      });
      toast.success(`${kind === "onboarding" ? "Onboarding" : "Offboarding"} aktualisiert (nur intern, Vertrag bleibt unverändert)`);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  if (readOnly) return null;

  if (editing) {
    return (
      <div className="mt-1 flex items-center gap-2 flex-wrap" data-testid={`${testId}-form`}>
        <input
          type="number" min="0" step="0.5" autoFocus
          value={ue}
          onChange={(e) => setUe(e.target.value)}
          className="w-16 font-mono text-xs border border-[#00CCC9] px-1 py-0.5 focus:outline-none"
          data-testid={`${testId}-ue-input`}
        />
        <span className="text-[10px] text-slate-500">UE</span>
        <select
          value={coach}
          onChange={(e) => setCoach(e.target.value)}
          className="text-[11px] border border-slate-300 px-1 py-0.5 bg-white"
          data-testid={`${testId}-coach-select`}
        >
          <option value="">— kein Coach —</option>
          {coaches.map((c) => (
            <option key={c.user_id} value={c.user_id}>{c.name}</option>
          ))}
        </select>
        <button onClick={save} disabled={saving} data-testid={`${testId}-save`} className="text-emerald-600 hover:text-emerald-700 disabled:opacity-50">
          <Check size={14} strokeWidth={2} />
        </button>
        <button onClick={() => setEditing(false)} data-testid={`${testId}-cancel`} className="text-slate-400 hover:text-slate-600">
          <X size={14} strokeWidth={2} />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={startEdit}
      data-testid={testId}
      title="UE-Ziel & Coach zuweisen (nur intern, ändert den Vertrag nicht)"
      className="text-[10px] text-slate-400 hover:text-[#0a788c] inline-flex items-center gap-1 mt-1"
    >
      <Pencil size={9} strokeWidth={1.5} /> UE / Coach zuweisen
    </button>
  );
}
